import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Button } from '@mm/components';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import {
  myLeadsExcludeTypes,
  MyLeadsFilterTypes,
  MyLeadsFriendlyName,
  myLeadsIncludeTypes,
  MyLeadsSearchQuery,
  SubsectionKeys,
} from '@mm/state/interfaces/myLeads';
import { companyFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { selectMyLeadsSearchOptions } from '@mm/state/selectors/myLeadsSelectors';
import {
  activeChatbotIdSelector,
  selectIsShopifyContext,
} from '@mm/state/selectors/viewSelectors';
import {
  Checkbox,
  ConfigProvider,
  Divider,
  InputNumberProps,
  Spin,
  Tooltip,
} from 'antd';
import classNames from 'classnames';
import { isEqual, omit, snakeCase, uniqueId } from 'lodash';
import React, {
  ReactNode,
  useCallback,
  useLayoutEffect,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { AgeSlider } from '../../GetLeads/LeadsSearch/LeadsSearchFiltersMenu/AgeSlider/AgeSlider';
import {
  convertToCamesCase,
  convertToUpperSnakeCase,
} from '../../GetLeads/LeadsSearch/LeadsSearchFiltersMenu/helpers';
import { YES_VALUE } from '../../GetLeads/LeadsSearch/LeadsSearchFiltersMenu/LeadsSearchFiltersMenu';
import { Section } from '../../GetLeads/LeadsSearch/LeadsSearchFiltersMenu/Section/Section';
import { Subsection } from '../../GetLeads/LeadsSearch/LeadsSearchFiltersMenu/Subsection/Subsection';
import {
  klaviyoEventsCamelizedMetricsMap,
  legacyMetrics,
} from '../../Signal/ManualSignal/ManualSignal';
import { FilterByLandingPage } from '../FilterByLandingPage/FilterByLandingPage';
import { DatesPicker } from './DatesPicker/DatesPicker';
import { EventsFilter } from './EventsFilter/EventsFilter';
import { remappedNames } from './helpers';
import { KlaviyoPurchaseFilter } from './KlaviyoPurchaseFilter/KlaviyoPurchaseFilter';
import styles from './MyLeadsFiltersPanel.module.css';
import { MyLeadsLocationInputFilter } from './MyLeadsLocationInputFilter/MyLeadsLocationInputFilter';
import { OtherAttributesFilter } from './OtherAttributesFilter/OtherAttributesFilter';
import { PlatformsFilter } from './PlatformsFilter/PlatformsFilter';
import { RadioGroup } from './RadioGroup/RadioGroup';
import { RadioTernary } from './RadioTernary/RadioTernary';
import { SearchContacts } from './SearchContacts/SearchContacts';
import { SuggestedAudiences } from './SuggestedAudiences/SuggestedAudiences';
import { TagsFilter } from './TagsFilter/TagsFilter';
import { useFilterParams } from './use-filter-params';

type Props = {
  title?: string;
  filterParams: MyLeadsSearchQuery;
  setFilterParams: (params: MyLeadsSearchQuery) => void;
  busy?: boolean;
  css?: {
    container?: string;
    suggestedAudiences?: string;
    filters?: string;
  };
};

export const MyLeadsFiltersPanel = React.forwardRef<
  HTMLDivElement | null,
  Props
>(({ title, filterParams, setFilterParams, busy, css }, ref) => {
  const isShopify = useSelector(selectIsShopifyContext);

  const [activePreset, setActivePreset] = useState('');
  const [scrollToId, setScrollToId] = useState('');
  const chatbotId = useSelector(activeChatbotIdSelector);
  const searchOptions = useSelector(selectMyLeadsSearchOptions);
  const hasLegacySignalMetrics = useSelector(
    companyFeatureFlagsSelector,
  ).legacy_signal_metrics;
  const signalMetricsMap = omit(
    klaviyoEventsCamelizedMetricsMap,
    hasLegacySignalMetrics ? [] : legacyMetrics,
  );
  const searchVariablesFiltered = Object.entries(
    searchOptions.variables || {},
  ).filter(item => item[1].length);
  const searchVariablesList = Object.fromEntries(searchVariablesFiltered);
  const searchAttributes = searchOptions.attributes;
  const minAge = 18;
  const maxAge = 100;
  const defaultAgeValue = filterParams?.query?.attributes?.age?.[0]
    ?.split('-')
    ?.map(i => Number(i)) as [number, number];
  const businessContactKey = convertToUpperSnakeCase(SubsectionKeys.leadType);
  const isExcludingBusinessContacts =
    !!filterParams.query?.excludedVariables?.[businessContactKey];
  const isIncludingBusinessContacts =
    !!filterParams.query?.variables?.[businessContactKey];

  const { primaryColor, isWhitelabelCompany, companyName } =
    useWhitelabelConfig();

  const otherProps = {
    activePreset,
    setScrollToId,
  };

  const history = useHistory();

  const {
    isFilterParamExist,
    getUpdatedFilterParams,
    hasActiveFilters,
    shouldDisableFilters,
    hasFilters,
  } = useFilterParams({ filterParams });

  const onCheckboxClick = useCallback(
    (key: string, value: string | boolean, type, isExisting) => {
      const updatedQuery = getUpdatedFilterParams({
        key,
        value,
        isExisting,
        type,
      });
      setFilterParams(updatedQuery);
    },
    [setFilterParams, getUpdatedFilterParams],
  );

  const withConfigProvider = (children: ReactNode) => {
    const colors = isWhitelabelCompany
      ? primaryColor
      : 'var(--color-geekblue-6)';
    return (
      <ConfigProvider
        theme={{
          components: {
            Checkbox: {
              colorPrimary: colors,
              colorPrimaryHover: colors,
            },
          },
        }}
      >
        {children}
      </ConfigProvider>
    );
  };

  const renderCheckbox = useCallback(
    (
      name: string,
      label: string,
      {
        isList = false,
        type = MyLeadsFilterTypes.variables,
        showOnlyWhenExists = true,
        isBoolean = false,
        variableName = null,
        filterValue,
      }: {
        isBoolean?: boolean;
        type?: MyLeadsFilterTypes;
        variableName?: string | null;
        isList?: boolean;
        showOnlyWhenExists?: boolean;
        filterValue?: any;
      } = {},
    ) => {
      if (
        showOnlyWhenExists &&
        (variableName || type !== MyLeadsFilterTypes.attributes)
      ) {
        const fieldname = variableName ? variableName : name;
        if (
          !Object.prototype.hasOwnProperty.call(searchVariablesList, fieldname)
        ) {
          return <></>;
        }
      }
      const remappedLabel = remappedNames(
        label,
        label === 'x-ray' && isWhitelabelCompany ? companyName : undefined,
      );

      const value = filterValue
        ? filterValue
        : isList
        ? type === MyLeadsFilterTypes.attributes
          ? remappedLabel.value
          : label
        : isBoolean
        ? true
        : YES_VALUE.toLocaleLowerCase();
      const isChecked = isFilterParamExist({
        key: name,
        value,
        type,
      });

      return withConfigProvider(
        <Checkbox
          key={uniqueId()}
          name={name}
          checked={isChecked}
          disabled={isChecked ? false : shouldDisableFilters}
          className={classNames(styles.checkbox, {
            [styles.listCheckbox]: isList,
          })}
          onClick={() => onCheckboxClick(name, value, type, isChecked)}
        >
          {remappedLabel.friendlyName}
        </Checkbox>,
      );
    },
    [
      searchVariablesList,
      onCheckboxClick,
      isFilterParamExist,
      shouldDisableFilters,
    ],
  );

  const renderCheckboxList = useCallback(
    (key: string, isAttribute?: boolean) => {
      if (isAttribute && searchAttributes) {
        return searchAttributes[key]?.map(checkbox =>
          renderCheckbox(key, checkbox, {
            isList: true,
            type: MyLeadsFilterTypes.attributes,
          }),
        );
      }
      return searchVariablesList[key]?.map(checkbox =>
        renderCheckbox(key, checkbox, { isList: true }),
      );
    },
    [renderCheckbox, searchVariablesList, searchAttributes],
  );

  const onSelectAllLeadType = () => {
    const updatedFilterParams = getUpdatedFilterParams({
      key: businessContactKey,
      value: true,
      isExisting: isExcludingBusinessContacts || isIncludingBusinessContacts,
      type: isExcludingBusinessContacts
        ? MyLeadsFilterTypes.excludedVariables
        : MyLeadsFilterTypes.variables,
    });

    setFilterParams(updatedFilterParams);
  };

  const withSpin = (children: JSX.Element | JSX.Element[]) => (
    <Spin size="small" spinning={busy} className={styles.spin}>
      {children}
    </Spin>
  );

  const hideGroupping = (keys: string | string[]) => {
    if (Array.isArray(keys)) {
      return !keys.some(key =>
        Object.prototype.hasOwnProperty.call(searchVariablesList, key),
      );
    }
    return !Object.prototype.hasOwnProperty.call(searchVariablesList, keys);
  };

  const onAfterAgeChange = (ageRange: [number, number]) => {
    const ageStr = isEqual(ageRange, [minAge, maxAge])
      ? ''
      : ageRange.join('-');
    const AGE_KEY = SubsectionKeys.age;

    if (filterParams.query?.attributes?.[AGE_KEY]) {
      delete filterParams.query?.attributes?.[AGE_KEY];
    }

    const updatedQuery = getUpdatedFilterParams({
      key: AGE_KEY,
      value: ageStr,
      isExisting: false,
      type: MyLeadsFilterTypes.attributes,
    });

    setFilterParams(updatedQuery);
  };

  const navigateToNativeIntegrations = () =>
    history.push(BotBuilderRoutes.integrations.getLink({ chatbotId }), {
      kind: 'native-integrations',
    });

  const onResetFilters = () => {
    setFilterParams({ query: {} });
  };

  const renderSubsection = ({
    name,
    key,
    types,
    renderAsList,
    skipHide,
    children,
  }: {
    name: string;
    key: string | string[];
    types: MyLeadsFilterTypes[];
    renderAsList: boolean;
    skipHide?: boolean;
    children?: any;
  }) => (
    <Subsection
      key={Array.isArray(key) ? key.join() : key}
      name={name}
      className={styles.subsection}
      hide={skipHide ? false : hideGroupping(key)}
      hasActiveFilters={hasActiveFilters({
        keys: Array.isArray(key) ? key : [key],
        types,
      })}
    >
      {renderAsList && !Array.isArray(key) ? (
        withSpin(
          renderCheckboxList(
            key,
            types.some(type => type === MyLeadsFilterTypes.attributes),
          ),
        )
      ) : children ? (
        withSpin(children)
      ) : (
        <></>
      )}
    </Subsection>
  );

  const renderSection = ({
    name,
    keys,
    isAttribute,
    children,
    skipHide,
  }: {
    name: string;
    keys: string[] | string[];
    isAttribute: boolean;
    children: any;
    skipHide?: boolean;
  }) => (
    <Section
      name={name}
      hide={skipHide ? false : hideGroupping(keys)}
      hasActiveFilters={hasActiveFilters({
        keys,
        types: [
          isAttribute
            ? MyLeadsFilterTypes.attributes
            : MyLeadsFilterTypes.variables,
        ],
      })}
    >
      {children}
    </Section>
  );

  const renderRadioTernary = useCallback(
    (
      attrKey: string,
      opts: {
        includeName?: string;
        excludeName?: string;
        ingoreName?: string;
      } = {},
    ) => (
      <div key={attrKey}>
        <RadioTernary
          attributeKey={attrKey}
          title={MyLeadsFriendlyName[attrKey]}
          filterParams={filterParams}
          setFilterParams={setFilterParams}
          includeName={opts.includeName || 'Include'}
          excludeName={opts.excludeName || 'Exclude'}
          ignoreName={opts.ingoreName || 'Ignore'}
        />
      </div>
    ),
    [filterParams, setFilterParams],
  );

  const renderRadioGroup = useCallback(
    (
      attrKey: string,
      inputProps?: InputNumberProps<number>,
      useExtendedOptions?: boolean,
    ) => (
      <div key={attrKey}>
        <RadioGroup
          attributeKey={attrKey}
          title={MyLeadsFriendlyName[attrKey]}
          filterParams={filterParams}
          setFilterParams={setFilterParams}
          useExtendedOptions={useExtendedOptions}
          inputProps={inputProps}
        />
      </div>
    ),
    [filterParams, setFilterParams],
  );

  const scrollToElement = () => {
    if (scrollToId) {
      document.getElementById(scrollToId)?.scrollIntoView(true);
    }
  };

  useLayoutEffect(() => scrollToElement(), [scrollToId]);

  return (
    <div className={classNames(styles.container, css?.container)}>
      <Section
        name="Suggested Audiences"
        css={{
          title: styles.basicFiltersTitle,
          heading: styles.basicFiltersHeading,
          expandedConatiner: classNames(
            styles.suggestedAudiences,
            css?.suggestedAudiences,
          ),
        }}
        arrowStyle="outlined"
        expandOnMount
      >
        <SuggestedAudiences
          filterParams={filterParams}
          setFilterParams={setFilterParams}
          activePreset={activePreset}
          setActivePreset={setActivePreset}
        />
      </Section>

      {title && (
        <div className={styles.title}>
          {title}
          {hasFilters && (
            <div className={styles.resetAll} onClick={onResetFilters}>
              Reset all <CloseOutlined />
            </div>
          )}
        </div>
      )}

      <div className={classNames(styles.filters, css?.filters)} ref={ref}>
        <div className={styles.searchBox}>
          <SearchContacts
            filterParams={filterParams}
            setFilterParams={setFilterParams}
            className={classNames({ [styles.disabled]: busy })}
          />
        </div>
        <Section
          name="Date Added"
          css={{
            title: styles.basicFiltersTitle,
            heading: styles.basicFiltersHeading,
          }}
          arrowStyle="outlined"
          hasActiveFilters={hasActiveFilters({
            keys: ['created_at'],
            types: [MyLeadsFilterTypes.attributes],
          })}
          expandOnMount
        >
          <DatesPicker
            filterParams={filterParams}
            setFilterParams={setFilterParams}
            className={classNames({ [styles.disabled]: busy })}
          />
        </Section>

        {!isShopify && (
          <Section
            name="Lead Type"
            css={{
              title: styles.basicFiltersTitle,
              heading: styles.basicFiltersHeading,
            }}
            arrowStyle="outlined"
            hasActiveFilters={hasActiveFilters({
              keys: [
                SubsectionKeys.leadType,
                SubsectionKeys.source,
                SubsectionKeys.type,
                SubsectionKeys.platforms,
              ],
              types: [
                MyLeadsFilterTypes.attributes,
                MyLeadsFilterTypes.variables,
                MyLeadsFilterTypes.excludedVariables,
              ],
            })}
          >
            {!isShopify && (
              <Subsection
                name="Lead Source"
                className={styles.subsection}
                openOnMount
              >
                {withSpin(renderCheckboxList(SubsectionKeys.source, true))}
              </Subsection>
            )}

            <Subsection
              name="Lead Creation Type"
              className={classNames(styles.subsection, styles.paddingBottom20)}
              openOnMount
            >
              {withSpin(
                ['New', 'Existing', 'Re-Signaled', 'Form Filled'].map(k => (
                  <div className={styles.checkboxRow} key={k}>
                    {renderCheckbox(SubsectionKeys.type, k, {
                      isList: true,
                      type: MyLeadsFilterTypes.attributes,
                      isBoolean: false,
                      variableName: SubsectionKeys.type,
                      showOnlyWhenExists: false,
                      filterValue: k === 'Form Filled' ? 'Form Fill' : k,
                    })}
                    {(k === 'Re-Signaled' || k === 'Existing') && (
                      <Tooltip
                        overlayClassName={styles.tooltip}
                        placement="bottom"
                        title={
                          <div>
                            {k === 'Re-Signaled'
                              ? `Re-signaled contacts are any contact that has been captured previously and 
                              has returned to your website in a subsequent visit and has new signal data.`
                              : `Existing contacts are visitors who are identified 
                              but already existed previously in your CRM.`}
                            <Button
                              css={{ container: styles.tooltipButton }}
                              color="transparent-no-shadow"
                              onClick={navigateToNativeIntegrations}
                            >
                              CRM Connections
                            </Button>
                          </div>
                        }
                      >
                        <InfoCircleOutlined className={styles.tooltipIcon} />
                      </Tooltip>
                    )}
                  </div>
                )),
              )}
            </Subsection>
            {!isShopify && (
              <>
                <Subsection
                  name="Lead Data"
                  className={styles.subsection}
                  openOnMount
                >
                  {withSpin(
                    <>
                      {withConfigProvider(
                        <Checkbox
                          key={uniqueId()}
                          name="allLeads"
                          checked={
                            !isExcludingBusinessContacts &&
                            !isIncludingBusinessContacts
                          }
                          disabled={shouldDisableFilters}
                          className={styles.checkbox}
                          onClick={onSelectAllLeadType}
                        >
                          All Leads
                        </Checkbox>,
                      )}
                      {renderCheckbox(
                        SubsectionKeys.leadType,
                        MyLeadsFriendlyName.CONSUMER_LEADS,
                        {
                          isList: false,
                          type: MyLeadsFilterTypes.excludedVariables,
                          isBoolean: true,
                          variableName: convertToCamesCase(
                            SubsectionKeys.leadType,
                          ),
                          showOnlyWhenExists: false,
                        },
                      )}
                      {withSpin(
                        renderCheckbox(
                          SubsectionKeys.leadType,
                          MyLeadsFriendlyName.BUSINESS_CONTACT,
                          {
                            isList: false,
                            type: MyLeadsFilterTypes.variables,
                            isBoolean: true,
                            variableName: convertToCamesCase(
                              SubsectionKeys.leadType,
                            ),
                            showOnlyWhenExists: false,
                          },
                        ),
                      )}
                    </>,
                  )}
                </Subsection>

                <Subsection
                  name="Platforms"
                  className={classNames(styles.subsection, styles.gap0)}
                  openOnMount
                >
                  <PlatformsFilter
                    attributeKey={SubsectionKeys.platforms}
                    filterParams={filterParams}
                    setFilterParams={setFilterParams}
                    checkboxDisabled={shouldDisableFilters}
                    css={{
                      checkbox: styles.checkbox,
                      label: styles.checkboxLabel,
                    }}
                  />
                </Subsection>
              </>
            )}
          </Section>
        )}
        {!isShopify && (
          <Section
            name="Interactions"
            css={{
              title: styles.basicFiltersTitle,
              heading: styles.basicFiltersHeading,
            }}
            arrowStyle="outlined"
            hasActiveFilters={hasActiveFilters({
              keys: [...SubsectionKeys.interactions, ...SubsectionKeys.intent],
              types: [MyLeadsFilterTypes.attributes],
            })}
          >
            <Subsection
              name="Interactions"
              className={styles.subsection}
              openOnMount
            >
              {withSpin(
                SubsectionKeys.interactions.map(key =>
                  renderCheckbox(key, MyLeadsFriendlyName[key], {
                    isList: false,
                    type: MyLeadsFilterTypes.attributes,
                    isBoolean: true,
                    variableName: convertToCamesCase(key),
                    showOnlyWhenExists: false,
                  }),
                ),
              )}
            </Subsection>
            <Subsection name="Intent" className={styles.subsection} openOnMount>
              {withSpin(
                SubsectionKeys.intent.map(key =>
                  renderCheckbox(key, MyLeadsFriendlyName[key], {
                    isList: false,
                    type: MyLeadsFilterTypes.attributes,
                    isBoolean: true,
                    variableName: convertToCamesCase(key),
                    showOnlyWhenExists: false,
                  }),
                ),
              )}
            </Subsection>
          </Section>
        )}

        <Section
          name="Customer Journey"
          css={{
            title: styles.basicFiltersTitle,
            heading: styles.basicFiltersHeading,
          }}
          arrowStyle="outlined"
          hasActiveFilters={hasActiveFilters({
            keys: [
              ...SubsectionKeys.customerJourney,
              SubsectionKeys.landingPageUrl,
              SubsectionKeys.urlsVisited,
            ],
            types: [...myLeadsIncludeTypes, ...myLeadsExcludeTypes],
          })}
          {...otherProps}
        >
          {withSpin(
            <>
              <EventsFilter
                filterParams={filterParams}
                setFilterParams={setFilterParams}
              />
              <Divider />
              <FilterByLandingPage
                attributeKey={SubsectionKeys.landingPageUrl}
                title="Landing Page URL"
                filterParams={filterParams}
                setFilterParams={setFilterParams}
                type="variables"
              />
              <Divider />
              <FilterByLandingPage
                title="Page browsed during visit"
                isLowercasedKey
                attributeKey={SubsectionKeys.urlsVisited}
                filterParams={filterParams}
                setFilterParams={setFilterParams}
                type="attributes"
              />
            </>,
          )}
        </Section>
        <Section
          name="Conversion Attributes"
          css={{
            title: styles.basicFiltersTitle,
            heading: styles.basicFiltersHeading,
          }}
          arrowStyle="outlined"
          hasActiveFilters={hasActiveFilters({
            keys: SubsectionKeys.revenueAttributes,
            types: [MyLeadsFilterTypes.attributes],
          })}
          {...otherProps}
        >
          <Subsection name="Klaviyo Purchases" className={styles.subsection}>
            {withSpin(
              <>
                <KlaviyoPurchaseFilter
                  filterParams={filterParams}
                  setFilterParams={setFilterParams}
                />
                <br />
                {renderRadioGroup('klaviyo_total_revenue', {
                  step: 0.1,
                  precision: 2,
                  addonAfter: '$',
                  maxLength: 7,
                  style: { width: '140px' },
                })}
                <br />
                {renderRadioGroup('klaviyo_post_email_revenue', {
                  step: 0.1,
                  precision: 2,
                  addonAfter: '$',
                  maxLength: 7,
                  style: { width: '140px' },
                })}
              </>,
            )}
          </Subsection>
          <Subsection name="Shopify Purchases" className={styles.subsection}>
            {withSpin(
              <>
                {renderRadioTernary('shopify_purchased', {
                  includeName: 'Purchased',
                  excludeName: 'No-Purchase',
                  ingoreName: 'Both',
                })}
                <br />
                {renderRadioGroup('shopify_total_revenue', {
                  step: 0.1,
                  precision: 2,
                  addonAfter: '$',
                  maxLength: 7,
                  style: { width: '140px' },
                })}
                <br />
                {renderRadioGroup(
                  'shopify_total_orders',
                  {
                    step: 1,
                    precision: 0,
                    maxLength: 7,
                    style: { width: '140px' },
                  },
                  true,
                )}
              </>,
            )}
          </Subsection>
        </Section>
        <Section
          name="Other Contact Attributes"
          css={{
            title: styles.basicFiltersTitle,
            heading: styles.basicFiltersHeading,
          }}
          arrowStyle="outlined"
          hasActiveFilters={false}
        >
          {withSpin(
            <OtherAttributesFilter
              filterParams={filterParams}
              setFilterParams={setFilterParams}
              disabled={shouldDisableFilters}
            />,
          )}
        </Section>

        <Section
          name="Tags"
          css={{
            title: styles.basicFiltersTitle,
            heading: styles.basicFiltersHeading,
          }}
          arrowStyle="outlined"
          hasActiveFilters={hasActiveFilters({
            keys: [SubsectionKeys.tags],
            types: [
              MyLeadsFilterTypes.attributes,
              MyLeadsFilterTypes.excludedAttributes,
            ],
          })}
        >
          {withSpin(
            <>
              <TagsFilter renderCheckbox={renderCheckbox} />
              <TagsFilter
                renderCheckbox={renderCheckbox}
                isExcluded
                css={{
                  container: styles.excludedTagsContainer,
                  title: styles.excludedTagsTitle,
                }}
              />
            </>,
          )}
        </Section>

        {!isShopify && (
          <Section
            name="Signal Events"
            css={{
              title: styles.basicFiltersTitle,
              heading: styles.basicFiltersHeading,
            }}
            arrowStyle="outlined"
            hasActiveFilters={hasActiveFilters({
              keys: [SubsectionKeys.signalEvents],
              types: [MyLeadsFilterTypes.attributes],
            })}
          >
            {withSpin(
              Object.values(signalMetricsMap)
                .map(key => snakeCase(key))
                .map(key =>
                  renderCheckbox(SubsectionKeys.signalEvents, key, {
                    isList: true,
                    type: MyLeadsFilterTypes.attributes,
                    showOnlyWhenExists: false,
                  }),
                ),
            )}
          </Section>
        )}

        {Boolean(searchVariablesFiltered.length) && (
          <div className={styles.title}>Advanced</div>
        )}
        {renderSection({
          name: 'Professional',
          keys: [
            SubsectionKeys.busOwner,
            SubsectionKeys.credRate,
            SubsectionKeys.income,
            SubsectionKeys.occupation,
            ...SubsectionKeys.profHobby,
          ],
          isAttribute: false,
          children: (
            <>
              {renderSubsection({
                name: MyLeadsFriendlyName.LD_BUS_OWNER,
                key: SubsectionKeys.busOwner,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}
              {renderSubsection({
                name: MyLeadsFriendlyName.LD_CREDRATE,
                key: SubsectionKeys.credRate,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}
              {renderSubsection({
                name: MyLeadsFriendlyName.LD_INCOMESTHH,
                key: SubsectionKeys.income,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}
              {renderSubsection({
                name: MyLeadsFriendlyName.LD_OCCUPATION,
                key: SubsectionKeys.occupation,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}
              {renderSubsection({
                name: 'Professional Hobby',
                key: SubsectionKeys.profHobby,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: false,
                children: (
                  <>
                    {renderCheckbox(
                      'ldInvReal',
                      MyLeadsFriendlyName.LD_INV_REAL,
                    )}
                    {renderCheckbox(
                      'ldInvStocks',
                      MyLeadsFriendlyName.LD_INV_STOCKS,
                    )}
                  </>
                ),
              })}
            </>
          ),
        })}

        {renderSection({
          name: 'Location & Home',
          keys: [
            ...SubsectionKeys.location,
            SubsectionKeys.homeValue,
            SubsectionKeys.homeFuel,
            SubsectionKeys.homeOwn,
          ],
          isAttribute: false,
          children: (
            <>
              {renderSubsection({
                name: MyLeadsFriendlyName.LD_HOMEMKTVAL,
                key: SubsectionKeys.homeValue,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}
              {renderSubsection({
                name: MyLeadsFriendlyName.LD_FUEL,
                key: SubsectionKeys.homeFuel,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}

              {renderSubsection({
                name: 'Location',
                key: SubsectionKeys.location,
                types: [
                  MyLeadsFilterTypes.variables,
                  MyLeadsFilterTypes.excludedVariables,
                ],
                renderAsList: false,
                skipHide: true,
                children: (
                  <>
                    <MyLeadsLocationInputFilter
                      disabled={shouldDisableFilters}
                      filterParams={filterParams}
                      setFilterParams={setFilterParams}
                    />
                    <div style={{ marginTop: '8px' }}>
                      <div style={{ fontWeight: 500 }}>Excluding Locations</div>
                      <MyLeadsLocationInputFilter
                        disabled={shouldDisableFilters}
                        filterParams={filterParams}
                        setFilterParams={setFilterParams}
                        exclude
                      />
                    </div>
                  </>
                ),
              })}

              {renderSubsection({
                name: MyLeadsFriendlyName.LD_HOMEOWNRNT,
                key: SubsectionKeys.homeOwn,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}
            </>
          ),
        })}

        {renderSection({
          name: 'Lifestyle',
          keys: SubsectionKeys.lifestyle,
          isAttribute: false,
          children: (
            <div>
              {renderCheckbox('ldColAntiq', MyLeadsFriendlyName.LD_COL_ANTIQ)}
              {renderCheckbox('ldColArts', MyLeadsFriendlyName.LD_COL_ARTS)}
              {renderCheckbox('ldBeauty', MyLeadsFriendlyName.LD_BEAUTY)}
              {renderCheckbox('ldBksmagBks', MyLeadsFriendlyName.LD_BKSMAG_BKS)}
              {renderCheckbox('ldCatowner', MyLeadsFriendlyName.LD_CATOWNER)}
              {renderCheckbox('ldDonation', MyLeadsFriendlyName.LD_DONATION)}
              {renderCheckbox('ldChGen', MyLeadsFriendlyName.LD_CH_GEN)}
              {renderCheckbox('ldColCoin', MyLeadsFriendlyName.LD_COL_COIN)}
              {renderCheckbox('ldDogowner', MyLeadsFriendlyName.LD_DOGOWNER)}
              {renderCheckbox('ldColSport', MyLeadsFriendlyName.LD_COL_SPORT)}
            </div>
          ),
        })}

        {renderSection({
          name: 'Individual',
          keys: [
            SubsectionKeys.age,
            SubsectionKeys.education,
            SubsectionKeys.gender,
            SubsectionKeys.lgbtSupport,
            SubsectionKeys.mrtlStatus,
            SubsectionKeys.numChild,
            SubsectionKeys.netWorth,
            SubsectionKeys.partyCode,
            SubsectionKeys.religion,
            ...SubsectionKeys.personal,
          ],
          isAttribute: false,
          children: (
            <>
              {renderSubsection({
                name: MyLeadsFriendlyName.age,
                key: SubsectionKeys.age,
                types: [MyLeadsFilterTypes.attributes],
                skipHide: true,
                renderAsList: false,
                children: (
                  <AgeSlider
                    minAge={minAge}
                    maxAge={maxAge}
                    disabled={shouldDisableFilters}
                    onAfterChange={onAfterAgeChange}
                    defaultValue={defaultAgeValue}
                    resetToDeafults={
                      !filterParams.query?.attributes?.[SubsectionKeys.age]
                    }
                  />
                ),
              })}

              {renderSubsection({
                name: MyLeadsFriendlyName.LD_EDUCATION,
                key: SubsectionKeys.education,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}

              {renderSubsection({
                name: MyLeadsFriendlyName.gender,
                key: SubsectionKeys.gender,
                types: [MyLeadsFilterTypes.attributes],
                skipHide: true,
                renderAsList: true,
              })}

              {renderSubsection({
                name: MyLeadsFriendlyName.LD_PRFL_LGBT_SUPPORT,
                key: SubsectionKeys.lgbtSupport,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}

              {renderSubsection({
                name: MyLeadsFriendlyName.LD_MRTLSTATUS,
                key: SubsectionKeys.mrtlStatus,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}

              {renderSubsection({
                name: MyLeadsFriendlyName.LD_NUMCHILD,
                key: SubsectionKeys.numChild,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}

              {renderSubsection({
                name: MyLeadsFriendlyName.LD_NETWORTH,
                key: SubsectionKeys.netWorth,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}

              {renderSubsection({
                name: MyLeadsFriendlyName.LD_PARTY_CODE,
                key: SubsectionKeys.partyCode,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}

              {renderSubsection({
                name: MyLeadsFriendlyName.LD_RELIGION,
                key: SubsectionKeys.religion,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: true,
              })}

              {renderSubsection({
                name: 'Personal',
                key: SubsectionKeys.personal,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: false,
                children: (
                  <div>
                    {renderCheckbox(
                      'ldDieting',
                      MyLeadsFriendlyName.LD_DIETING,
                    )}
                    {renderCheckbox(
                      'ldPrflGunControl',
                      MyLeadsFriendlyName.LD_PRFL_GUN_CONTROL,
                    )}
                    {renderCheckbox(
                      'ldPrflMarijuanaReform',
                      MyLeadsFriendlyName.LD_PRFL_MARIJUANA_REFORM,
                    )}
                    {renderCheckbox(
                      'ldPrflMilSupport',
                      MyLeadsFriendlyName.LD_PRFL_MIL_SUPPORT,
                    )}
                    {renderCheckbox(
                      'ldSingparent',
                      MyLeadsFriendlyName.LD_SINGPARENT,
                    )}
                  </div>
                ),
              })}
            </>
          ),
        })}

        {renderSection({
          name: 'Behaviour',
          keys: [...SubsectionKeys.interests, ...SubsectionKeys.sports],
          isAttribute: false,
          children: (
            <>
              {renderSubsection({
                name: 'Interests',
                key: SubsectionKeys.interests,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: false,
                children: (
                  <div>
                    {renderCheckbox(
                      'ldDonEnvir',
                      MyLeadsFriendlyName.LD_DON_ENVIR,
                    )}
                    {renderCheckbox(
                      'ldFoodWine',
                      MyLeadsFriendlyName.LD_FOOD_WINE,
                    )}
                    {renderCheckbox(
                      'ldFoodsnat',
                      MyLeadsFriendlyName.LD_FOODSNAT,
                    )}
                    {renderCheckbox(
                      'ldGamBoard',
                      MyLeadsFriendlyName.LD_GAM_BOARD,
                    )}
                    {renderCheckbox(
                      'ldGamCasino',
                      MyLeadsFriendlyName.LD_GAM_CASINO,
                    )}
                    {renderCheckbox(
                      'ldGamComp',
                      MyLeadsFriendlyName.LD_GAM_COMP,
                    )}
                    {renderCheckbox('ldGamVid', MyLeadsFriendlyName.LD_GAM_VID)}
                    {renderCheckbox('ldGaming', MyLeadsFriendlyName.LD_GAMING)}
                    {renderCheckbox(
                      'ldMotorcycle',
                      MyLeadsFriendlyName.LD_MOTORCYCLE,
                    )}
                    {renderCheckbox(
                      'ldScispace',
                      MyLeadsFriendlyName.LD_SCISPACE,
                    )}
                    {renderCheckbox(
                      'ldScubadiver',
                      MyLeadsFriendlyName.LD_SCUBADIVER,
                    )}
                    {renderCheckbox(
                      'ldSmoking',
                      MyLeadsFriendlyName.LD_SMOKING,
                    )}
                  </div>
                ),
              })}

              {renderSubsection({
                name: 'Sports',
                key: SubsectionKeys.sports,
                types: [MyLeadsFilterTypes.variables],
                renderAsList: false,
                children: (
                  <div>
                    {renderCheckbox(
                      'ldSpecBase',
                      MyLeadsFriendlyName.LD_SPEC_BASE,
                    )}
                    {renderCheckbox(
                      'ldBoating',
                      MyLeadsFriendlyName.LD_BOATING,
                    )}
                    {renderCheckbox(
                      'ldSpecHock',
                      MyLeadsFriendlyName.LD_SPEC_HOCK,
                    )}
                    {renderCheckbox('ldGolf', MyLeadsFriendlyName.LD_GOLF)}
                    {renderCheckbox('ldNascar', MyLeadsFriendlyName.LD_NASCAR)}
                    {renderCheckbox(
                      'ldSnowski',
                      MyLeadsFriendlyName.LD_SNOWSKI,
                    )}
                    {renderCheckbox(
                      'ldSpecSocc',
                      MyLeadsFriendlyName.LD_SPEC_SOCC,
                    )}
                    {renderCheckbox('ldTennis', MyLeadsFriendlyName.LD_TENNIS)}
                  </div>
                ),
              })}
            </>
          ),
        })}
      </div>
    </div>
  );
});
